import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Card, Tag, Typography } from 'antd';
import { StarFilled, BranchesOutlined, MessageOutlined, LockOutlined, EditOutlined, ClockCircleOutlined, PartitionOutlined, SwapOutlined, TagsOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const REPLY_TYPE_COLORS = {
    'keyword-fork': 'blue',
    'email': 'green',
    'date': 'purple',
    'time': 'orange',
    'number': 'cyan',
    'options': 'magenta',
    'cards': 'gold',
    'wit-ai': 'red',
    'zip-recruiter': 'lime',
    'zip-code': 'geekblue',
    'send-details': 'volcano',
    'send-details-grp': 'volcano',
    'send-details-all': 'volcano',
    'dept-location': 'purple',
    'career-type': 'purple',
    'business-type': 'purple',
    'event': 'orange',
    'calendly': 'blue',
    'time-matching': 'orange',
    'event-cancel': 'red',
};

const ModuleNode = memo(({ data }) => {
    const { module, isStartModule, isEditable, isSelected, displayResponse, extras = {} } = data;
    const connections = module.connection || [];
    const triggers = (module.triggers || []).filter((t) => !t.deleted_at);
    const hasFollowups = module.followups && module.followups.length > 0;
    const replyType = module.reply_type || '';
    const replyTypeColor = REPLY_TYPE_COLORS[replyType] || 'default';
    const pathForksCount = (extras.pathForks && Array.isArray(module.path_forks)) ? module.path_forks.length : 0;
    const botRedirectCount = (extras.botRedirect && Array.isArray(module.bot_redirect)) ? module.bot_redirect.length : 0;
    const tagsCount = (extras.tags && Array.isArray(module.tags)) ? module.tags.length : 0;

    const responseText = displayResponse || module.response;

    const nodeClasses = [
        'module-node',
        isStartModule ? 'start-module' : '',
        isEditable ? 'module-node-editable' : '',
        isSelected ? 'module-node-selected' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={nodeClasses}>
            {/* Target handle — incoming connections */}
            <Handle
                type="target"
                position={Position.Left}
                className="module-handle target-handle"
                isConnectable={false}
            />

            {/* Self-loop target handle — top */}
            <Handle
                type="target"
                position={Position.Top}
                id="self-target"
                className="module-handle target-handle"
                isConnectable={false}
            />

            <Card
                size="small"
                className="module-node-card"
                title={
                    <div className="module-node-header">
                        {isStartModule && (
                            <StarFilled style={{ color: '#52c41a', marginRight: 6, fontSize: 14 }} />
                        )}
                        <Text strong ellipsis style={{ maxWidth: isEditable ? 140 : 160, fontSize: 13 }}>
                            {module.title}
                        </Text>
                        {isEditable && (
                            <EditOutlined style={{ marginLeft: 6, fontSize: 12, color: '#4CAF50' }} />
                        )}
                    </div>
                }
                extra={
                    replyType ? (
                        <Tag color={replyTypeColor} style={{ fontSize: 10, marginRight: 0 }}>
                            {replyType}
                        </Tag>
                    ) : null
                }
            >
                {responseText && (
                    <Paragraph
                        ellipsis={{ rows: 2 }}
                        className="module-node-response"
                        style={{ marginBottom: module.response_append ? 4 : undefined }}
                    >
                        <MessageOutlined style={{ marginRight: 4, opacity: 0.5 }} />
                        {responseText}
                    </Paragraph>
                )}

                {module.response_append && (
                    <Paragraph
                        ellipsis={{ rows: 2 }}
                        className="module-node-response-append"
                        style={{ fontSize: 11, color: '#8c8c8c' }}
                    >
                        <LockOutlined style={{ marginRight: 4, color: '#cf1322' }} />
                        {module.response_append}
                    </Paragraph>
                )}

                {module.field && (
                    <div className="module-node-field">
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            saves to: <Tag style={{ fontSize: 10 }}>{module.field}</Tag>
                        </Text>
                    </div>
                )}

                {connections.length > 0 && (
                    <div className="module-node-connections">
                        <BranchesOutlined style={{ fontSize: 11, opacity: 0.5, marginRight: 4 }} />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {connections.length} connection{connections.length > 1 ? 's' : ''}
                        </Text>
                    </div>
                )}

                {triggers.length > 0 && (
                    <div className="module-node-triggers">
                        <ClockCircleOutlined style={{ fontSize: 11, opacity: 0.5, marginRight: 4, color: '#f5222d' }} />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {triggers.length} trigger{triggers.length > 1 ? 's' : ''}
                        </Text>
                    </div>
                )}

                {hasFollowups && (
                    <div className="module-node-followups">
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            ⏱ {module.followups.length} follow-up{module.followups.length > 1 ? 's' : ''}
                        </Text>
                    </div>
                )}

                {(pathForksCount > 0 || botRedirectCount > 0 || tagsCount > 0) && (
                    <div className="module-node-extras" style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {pathForksCount > 0 && (
                            <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>
                                <PartitionOutlined style={{ marginRight: 2 }} />{pathForksCount}
                            </Tag>
                        )}
                        {botRedirectCount > 0 && (
                            <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                                <SwapOutlined style={{ marginRight: 2 }} />{botRedirectCount}
                            </Tag>
                        )}
                        {tagsCount > 0 && (
                            <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>
                                <TagsOutlined style={{ marginRight: 2 }} />{tagsCount}
                            </Tag>
                        )}
                    </div>
                )}
            </Card>

            {/* Per-connection source handles on the right */}
            {connections.map((conn, idx) => (
                <Handle
                    key={conn.goto}
                    type="source"
                    position={Position.Right}
                    id={`conn-${conn.goto}`}
                    className="module-handle connection-handle"
                    isConnectable={false}
                    style={{
                        top: `${30 + idx * 18}%`,
                    }}
                />
            ))}

            {/* Fallback handle */}
            {module.fallback && (
                <Handle
                    type="source"
                    position={Position.Bottom}
                    id="fallback"
                    className="module-handle fallback-handle"
                    isConnectable={false}
                />
            )}

            {/* Per-trigger source handles on the right (below connections) */}
            {triggers.map((trigger, idx) => (
                <Handle
                    key={trigger.id}
                    type="source"
                    position={Position.Right}
                    id={`trigger-${trigger.id}`}
                    className="module-handle trigger-handle"
                    isConnectable={false}
                    style={{
                        top: `${30 + (connections.length + idx) * 18}%`,
                    }}
                />
            ))}
        </div>
    );
});

ModuleNode.displayName = 'ModuleNode';

export default ModuleNode;
